import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import VideoCall from "@/components/VideoCall";
import { toast } from "sonner";
import { Video, Loader2, Calendar, PhoneCall } from "lucide-react";

export default function Videos() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slotOpen, setSlotOpen] = useState(false);
  const [slots, setSlots] = useState(null);
  const [ccName, setCcName] = useState("");
  const [chosen, setChosen] = useState(null);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [circle, setCircle] = useState([]);
  const isStudent = user.role === "Student";
  const isCC = ["Campus Coordinator", "Super Admin"].includes(user.role);
  const canCall = ["Campus Coordinator", "Super Admin", "Clinical Instructor"].includes(user.role);

  const load = () => api.get("/video-meetings").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    if (params.get("request") === "1" && isStudent) openSlots();
    return () => clearInterval(t);
  }, []);

  const openAdhoc = async () => {
    setAdhocOpen(true);
    try { const { data } = await api.get("/messages/recipients"); setCircle(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startAdhoc = async (recipient) => {
    setLoading(true);
    try {
      const { data } = await api.post("/video-meetings/adhoc", { recipient_user_id: recipient.id });
      toast.success(`Calling ${recipient.name}…`); setAdhocOpen(false);
      const tok = await api.post(`/video-meetings/${data.id}/token`);
      setActive({ id: data.id, roomUrl: tok.data.room_url, token: tok.data.token, isOwner: tok.data.is_owner });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openSlots = async () => {
    setSlotOpen(true); setSlots(null); setChosen(null);
    try {
      const { data } = await api.get("/video-meetings/available-slots");
      setSlots(data.slots); setCcName(data.coordinator_name || "your coordinator");
      if (!data.has_coordinator) toast.error("No coordinator is assigned to you yet.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSlots([]); }
  };

  const confirmSlot = async () => {
    if (!chosen) return toast.error("Pick a time");
    try {
      await api.post("/video-meetings/request", { scheduled_start: chosen });
      toast.success("Video call requested"); setSlotOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startCall = async (v) => {
    setLoading(true);
    try { await api.post(`/video-meetings/${v.id}/start`); toast.success("Call started — student notified"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const joinCall = async (v) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/video-meetings/${v.id}/token`);
      setActive({ id: v.id, roomUrl: data.room_url, token: data.token, isOwner: data.is_owner });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const closeCall = async () => {
    if (active) { try { await api.post(`/video-meetings/${active.id}/end`); } catch {} }
    setActive(null); load();
  };

  // group slots by day
  const byDay = (slots || []).reduce((acc, s) => {
    const d = new Date(s).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    (acc[d] = acc[d] || []).push(s); return acc;
  }, {});

  return (
    <div>
      <PageHeader title="Video Calls" subtitle="Daily.co video appointments with your campus circle">
        {isStudent && <Button onClick={openSlots} className="bg-cc-green text-white" data-testid="request-call-btn">+ Request Video Call</Button>}
        {canCall && <Button onClick={openAdhoc} className="bg-cc-blue text-white" data-testid="start-adhoc-call-btn"><PhoneCall className="w-4 h-4 mr-1" />Start Video Call</Button>}
      </PageHeader>

      <Card className="overflow-hidden">
        {rows.length === 0 ? <EmptyState text="No video appointments yet." /> : (
          <div className="space-y-3">
            {rows.map((v) => (
              <div key={v.id} className={`flex items-center justify-between border rounded-md p-4 ${v.status === "Started" ? "cc-flash border-cc-orange" : ""}`} data-testid={`video-${v.id}`}>
                <div className="flex items-center gap-3">
                  <Video className={`w-5 h-5 ${v.status === "Started" ? "text-cc-orange" : "text-zinc-400"}`} />
                  <div>
                    <p className="font-medium text-zinc-800 text-sm">{v.adhoc ? (v.recipient_name && v.host_name ? `${v.host_name} ↔ ${v.recipient_name}` : "Instant video call") : "Video appointment"}</p>
                    <p className="text-xs text-zinc-400">{v.scheduled_start ? new Date(v.scheduled_start).toLocaleString() : "—"}{v.adhoc ? " · Ad-hoc" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={v.status} />
                  {isCC && ["Scheduled"].includes(v.status) && <Button size="sm" onClick={() => startCall(v)} disabled={loading} className="bg-cc-blue text-white" data-testid={`start-${v.id}`}>Start Call</Button>}
                  {v.status === "Started" && <Button size="sm" onClick={() => joinCall(v)} disabled={loading} className="bg-cc-orange text-white" data-testid={`join-${v.id}`}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={slotOpen} onOpenChange={setSlotOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>Choose a time with {ccName}</DialogTitle></DialogHeader>
          {slots === null ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cc-blue" /></div> :
            slots.length === 0 ? <EmptyState text="No available times. Please check back later." /> : (
              <div className="space-y-4">
                <p className="text-xs text-zinc-400 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Earliest available is the next business day.</p>
                {Object.entries(byDay).map(([day, items]) => (
                  <div key={day}>
                    <p className="text-xs font-semibold uppercase text-zinc-500 mb-2">{day}</p>
                    <div className="flex flex-wrap gap-2">
                      {items.map((s) => (
                        <button key={s} onClick={() => setChosen(s)} data-testid={`slot-${s}`}
                          className={`px-3 py-1.5 rounded-md border text-sm ${chosen === s ? "bg-cc-blue text-white border-cc-blue" : "hover:bg-zinc-50"}`}>
                          {new Date(s).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotOpen(false)}>Cancel</Button>
            <Button onClick={confirmSlot} disabled={!chosen} className="bg-cc-green text-white" data-testid="confirm-slot-btn">Request {chosen ? `(${new Date(chosen).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })})` : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {active && <VideoCall roomUrl={active.roomUrl} token={active.token} userName={user.name} isOwner={active.isOwner} onClose={closeCall} />}

      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Start a video call</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Choose someone in your campus circle. They'll get an alert to join right away.</p>
          <Command>
            <CommandInput placeholder="Search by name…" data-testid="adhoc-search" />
            <CommandList>
              <CommandEmpty>No one in your circle yet.</CommandEmpty>
              <CommandGroup>
                {circle.map((p) => (
                  <CommandItem key={p.id} value={`${p.name} ${p.email}`} onSelect={() => startAdhoc(p)} data-testid={`adhoc-opt-${p.id}`} disabled={loading}>
                    <div><p className="text-sm">{p.name}</p><p className="text-xs text-zinc-400">{p.role} · {p.email}</p></div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  );
}
